import styled from "styled-components";
import { theme } from "../../styles/appTheme";
import { images } from "./constants";

export const HeroSection = styled.section({
  position: "relative",
  height: 550,
  overflow: "hidden",
  padding: "24px 16px",
});

export const HeroBackground = styled.img({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const HeroOverlay = styled.div({
  position: "absolute",
  inset: 0,
});

export const HeroContent = styled.div({
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

export const HeroCatch = styled.p({
  margin: "0 0 16px",
  textAlign: "center",
  color: theme.palette.white,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "17px",
  whiteSpace: "nowrap",
});

export const HeroRibbon = styled.p({
  margin: 0,
  width: 264,
  height: 29,
  padding: "7px 0 0",
  background: `url(${images.heroRibbon}) no-repeat`,
  color: theme.palette.main,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1,
  textAlign: "center",
  whiteSpace: "nowrap",
});

export const HeroLogo = styled.img({
  display: "block",
  width: 253,
  margin: "16px 0 0",
});

// Figma では 350x464 の枠に写真を引き伸ばして -8.22° 傾けて配置している（狭い画面でも顔が切れないよう右端基準）
export const HeroModel = styled.img({
  position: "absolute",
  top: 172,
  right: -75,
  zIndex: 2,
  width: 350,
  height: 464,
  maxWidth: "none",
  transform: "rotate(-8.22deg)",
});

export const HeroPoints = styled.div({
  position: "absolute",
  inset: 0,
  zIndex: 3,
  pointerEvents: "none",
});

export const HeroPointBadge = styled.div({
  position: "absolute",
  left: 22,
  color: theme.palette.white,
});

export const HeroPointRing = styled.img({
  display: "block",
});

export const HeroPointInner = styled.div({
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  textAlign: "center",
});

export const HeroPointLabel = styled.span({
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.2,
});

// 末尾の字間を打ち消して、字間を含めない Figma と同じ中心に揃える
export const HeroPointValue = styled.span({
  display: "block",
  marginRight: "-0.1em",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "0.1em",
});

export const HeroPointNumber = styled.span({
  fontFamily: '"Roboto", "Noto Sans JP", sans-serif',
  letterSpacing: 0,
});

export const HeroCredit = styled.p({
  position: "absolute",
  right: 14,
  bottom: 11,
  zIndex: 4,
  margin: 0,
  color: theme.palette.white,
  fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: "0.9px",
  textAlign: "right",
  textShadow: "0 0 4px #000, 0 0 4px #000",
});

export const Cta01SectionRoot = styled.section({
  background: theme.palette.bg,
  padding: "24px 12px 40px",
});

export const Cta02SectionRoot = styled.section({
  background: theme.palette.bgBlue,
  padding: "16px 12px 40px",
});

export const PointSectionRoot = styled.section({
  background: theme.palette.main,
  padding: "40px 16px 24px",
});

export const PointSectionHeading = styled.h2({
  margin: "0",
  color: theme.palette.white,
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1.5,
  letterSpacing: 1.2,
  textAlign: "center",
});

export const PointSectionHeadingAccent = styled.span({
  color: theme.palette.lemon,
  fontWeight: 900,
  display: "inline-flex",
  marginTop: 16,
  letterSpacing: 1.2,
});

export const PointSectionHeadingAccentChar = styled.span({
  position: "relative",
  display: "inline-block",
  paddingTop: 2,
  "&::before": {
    content: "\"\"",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    top: 0,
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: theme.palette.white,
  },
});

export const PointSectionFlowFrame = styled.div({
  marginTop: 24,
  borderRadius: 16,
  background: theme.palette.white,
  marginBottom: 16,
});

export const PointSectionFlowSystemImage = styled.img({
  width: "100%",
  height: "auto",
  display: "block",
});

export const PointSectionPointHeader = styled.div<{ $backgroundImage: string }>(({ $backgroundImage }) => ({
  position: "relative",
  marginTop: 40,
  color: theme.palette.main,
  fontSize: 18,
  fontWeight: 700,
  textAlign: "center",
  padding: "7px 29px",
  backgroundImage: `url(${$backgroundImage})`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));


export const PointSectionPointList = styled.div({
  marginTop: 16,
  display: "grid",
  gap: 8,
});

export const PointSectionPointCard = styled.div({
  position: "relative",
  background: theme.palette.white,
  borderRadius: 16,
  height: 112,
  padding: "24px",
  display: "flex",
  alignItems: "center",
  gap: 20,
});

export const PointSectionPointIcon = styled.img({
  width: 64,
  height: 64,
  objectFit: "contain",
  flexShrink: 0,
});

export const PointSectionPointText = styled.p({
  margin: 0,
  color: theme.palette.text,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  whiteSpace: "pre-line",
  zIndex: 1,
});

export const PointSectionPointAccentBlue = styled.span({
  color: theme.palette.main,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.5,
});

export const PointSectionPointAccent = styled.span({
  color: theme.palette.text,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.5,
});

export const PointSectionPointNumber = styled.span({
  position: "absolute",
  right: 6,
  bottom: 6,
  color: theme.palette.main,
  opacity: 0.15,
  fontSize: 44,
  fontWeight: 700,
  lineHeight: 1,
});

export const PointSectionDisclaimer = styled.p({
  margin: "4px 0 0",
  color: theme.palette.white,
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1.5,
});

export const DifferenceSectionRoot = styled.section({
  background: theme.palette.bg,
  padding: "40px 12px",
});

export const DifferenceSectionTitle = styled.h2({
  margin: "0",
  color: theme.palette.text,
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1.5,
  letterSpacing: 1.2,
  textAlign: "center",
});

export const DifferenceSectionBody = styled.div({
  display: "grid",
  marginTop: 16,
  gridTemplateColumns: "33px minmax(0, 1fr) 167px",
  gap: 0,
});

const DifferenceSectionLeftLabel = styled.div({
  color: theme.palette.main,
  fontSize: 14,
  fontWeight: 900,
  lineHeight: 1.2,
  textAlign: "center",
  whiteSpace: "pre-line",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 0",
});

export const DifferenceSectionLeftHeaderLabel = styled(DifferenceSectionLeftLabel)({
  minHeight: 86,
});

export const DifferenceSectionLeftRowLabel = styled(DifferenceSectionLeftLabel)({
  minHeight: 104,
  borderTop: `1px dashed ${theme.palette.divider}`,
});

export const DifferenceSectionLeftHeaderCell = styled.div({
  minHeight: 86,
  color: theme.palette.gray2,
  fontSize: 18,
  fontWeight: 500,
  lineHeight: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 0",
  textAlign: "center",
  whiteSpace: "pre-line",
});

export const DifferenceSectionLeftCell = styled.div({
  minHeight: 104,
  color: theme.palette.text,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 0",
  textAlign: "center",
  whiteSpace: "pre-line",
  borderTop: `1px dashed ${theme.palette.divider}`,
});

export const DifferenceSectionRightHeader = styled.div({
  minHeight: 86,
  background: theme.palette.accent,
  color: theme.palette.white,
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  borderRadius: "18px 18px 0 0",
});

export const DifferenceSectionRightCell = styled.div<{ $isLast?: boolean }>(({ $isLast }) => ({
  minHeight: 104,
  background: theme.palette.main,
  color: theme.palette.white,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "pre-line",
  // 「専任コンシェルジュ」（16px × 9 文字 = 144px）が 1 行に収まるよう左右の余白を詰める
  padding: "8px",
  borderTop: `1px dashed ${theme.palette.divider}`,
  borderRadius: $isLast ? "0 0 18px 18px" : undefined,
}));

export const WhySectionRoot = styled.section({
  background: theme.palette.main,
  padding: "40px 16px",
});

export const WhySectionBubble = styled.div<{ $backgroundImage: string }>(({ $backgroundImage }) => ({
  position: "relative",
  height: 123,
  backgroundImage: `url(${$backgroundImage})`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));

export const WhySectionBubbleText = styled.p({
  position: "absolute",
  top: 18,
  left: 0,
  right: 0,
  margin: 0,
  color: theme.palette.text,
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1.5,
  letterSpacing: 1.2,
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
});

export const WhySectionBubbleAccent = styled.span({
  color: theme.palette.accent,
});

export const WhySectionCardList = styled.div({
  marginTop: 16,
  display: "grid",
  gap: 16,
});

export const WhySectionCard = styled.article<{ $flushRight?: boolean; $flushLeft?: boolean }>(
  ({ $flushRight, $flushLeft }) => ({
  background: theme.palette.white,
  borderRadius: 16,
  padding: 24,
  ...($flushRight
    ? {
        marginRight: -16,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      }
    : {}),
  ...($flushLeft
    ? {
        marginLeft: -16,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      }
    : {}),
}));

export const WhySectionCardTitle = styled.h3({
  margin: 0,
  color: theme.palette.main,
  textAlign: "center",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.5,
});

export const WhySectionCardImage = styled.img({
  margin: "16px auto 0",
  width: "auto",
  height: 140,
  display: "block",
});

export const WhySectionCardText = styled.p({
  margin: "16px 0 0 0",
  color: theme.palette.text,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.8,
});

export const ReasonSectionRoot = styled.section({
  background: theme.palette.bg,
  padding: "40px 16px",
});

export const ReasonSectionTitle = styled.h2({
  margin: 0,
  color: theme.palette.text,
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1.5,
  letterSpacing: 1.2,
  textAlign: "center",
});

export const ReasonSectionCard = styled.article({
  marginTop: 24,
  background: theme.palette.white,
  borderRadius: 16,
  padding: 24,
});

export const ReasonSectionCardHeading = styled.h3({
  margin: 0,
  color: theme.palette.text,
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.5,
  display: "flex",
  alignItems: "center",
  gap: 12,
});

export const ReasonSectionCardHeadingDot = styled.span({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: theme.palette.main,
});

export const ReasonSectionText = styled.p({
  margin: "16px 0 0 0",
  color: theme.palette.text,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.8,
  whiteSpace: "pre-line",
});

export const ReasonSectionSystemImage = styled.img({
  width: "auto",
  height: 113,
  display: "block",
  margin: "16px 0 0 0",
});

export const FaqSectionRoot = styled.section({
  background: theme.palette.white,
  padding: "40px 16px",
});

export const FaqSectionTitle = styled.h2({
  margin: 0,
  color: theme.palette.text,
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1.5,
  letterSpacing: 1.2,
  textAlign: "center",
});

export const FaqSectionList = styled.div({
  marginTop: 24,
  display: "grid",
  gap: 16,
});

export const FaqSectionQuestionButton = styled.button({
  width: "100%",
  border: 0,
  background: theme.palette.main,
  color: theme.palette.white,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  cursor: "pointer",
  textAlign: "left",
});

export const FaqSectionQuestionLead = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 12,
  minWidth: 0,
});

export const FaqSectionQuestionQ = styled.span({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1,
  flex: "0 0 auto",
});

export const FaqSectionQuestionText = styled.span({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.8,
});

export const FaqSectionQuestionToggle = styled.span({
  fontSize: 24,
  lineHeight: 1,
  fontWeight: 500,
  flex: "0 0 auto",
});

export const FaqSectionAnswer = styled.p({
  margin: 0,
  padding: "10px 16px",
  color: theme.palette.text,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.8,
  whiteSpace: "pre-line",
});

export const FaqSectionAnswerWrap = styled.div<{ $open: boolean }>(({ $open }) => ({
  display: "grid",
  gridTemplateRows: $open ? "1fr" : "0fr",
  transition: "grid-template-rows 240ms ease, opacity 200ms ease",
  opacity: $open ? 1 : 0,
}));

export const FaqSectionAnswerInner = styled.div({
  overflow: "hidden",
});

export const Cta01SectionLeadRibbon = styled.div<{ $backgroundImage: string }>(({ $backgroundImage }) => ({
  position: "relative",
  color: theme.palette.white,
  fontSize: 14,
  fontWeight: 700,
  // Figma は文字の左端が帯から 11px。「複数社の〜」24 文字（336px）が 390px 幅で 1 行に収まる
  padding: "10px 16px 10px 11px",
  backgroundImage: `url(${$backgroundImage})`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));


export const FreeCheckCard = styled.div({
  marginTop: 21,
  position: "relative",
  background: theme.palette.main,
  border: `3px solid ${theme.palette.main}`,
  borderRadius: 8,
  overflow: "visible",
  boxShadow: `0 0 8px  0 ${theme.palette.shadowGrey}`,
});

export const FreeCheckBadge = styled.div({
  position: "absolute",
  top: -14,
  left: "50%",
  transform: "translateX(-50%)",
  background: theme.palette.white,
  color: theme.palette.main,
  border: `2px solid ${theme.palette.main}`,
  borderRadius: 999,
  padding: "4px 15px",
  fontSize: 12,
  fontWeight: 700,
  zIndex: 2,
});

export const FreeCheckTitle = styled.div({
  background: theme.palette.main,
  color: theme.palette.white,
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0.8,
  textAlign: "center",
  padding: "19px 12px",
});

export const FreeCheckForm = styled.form({
  background: theme.palette.white,
  borderBottomLeftRadius: 8,
  borderBottomRightRadius: 8,
  padding: "16px",
  display: "grid",
  gap: 8,
});

export const FreeCheckInput = styled.input({
  background: theme.palette.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
  height: 50,
  padding: "10px 16px",
  width: "100%",
  color: theme.palette.text,
  fontSize: 14,
  fontWeight: 500,
  outline: "none",
  "::placeholder": {
    color: theme.palette.gray,
  },
});

export const FreeCheckNumberField = styled.div({
  position: "relative",
});

export const FreeCheckNumberInput = styled(FreeCheckInput)({
  paddingRight: 40,
});

export const FreeCheckInputSuffix = styled.span({
  color: theme.palette.text,
  fontSize: 14,
  fontWeight: 700,
  position: "absolute",
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
});

export const FreeCheckCta = styled.button<{ $backgroundImage: string }>(({ $backgroundImage }) => ({
  marginTop: 8,
  border: 0,
  width: "100%",
  height: 56,
  backgroundColor: theme.palette.white,
  backgroundImage: `url(${$backgroundImage})`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  color: theme.palette.white,
  fontSize: 16,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  cursor: "pointer",
}));

export const FooterSection = styled.footer({
  display: "grid",
  background: theme.palette.main,
  color: theme.palette.white,
  padding: "40px 20px",
  gap: 10,
});

export const FooterLogo = styled.img({
  height: 20,
});

export const FooterMetaRow = styled.div({
  display: "grid",
  gridTemplateColumns: "1.5fr 1fr",
  gap: 24,
  textAlign: "left",
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1.2,
});

export const FooterPolicyLink = styled.a({
  color: theme.palette.white,
  textDecoration: "underline",
  fontSize: 10,
  fontWeight: 500,
  display: "inline-block",
  textAlign: "center",
});

export const FooterCopyright = styled.small({
  display: "block",
  fontSize: 10,
  fontWeight: 400,
  textAlign: "center",
});
